import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Percent, Plus, Search, Edit2, Trash2, TrendingUp, FileText } from 'lucide-react';
import apiService from '../../services/api';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import Table from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

interface ProfitMargin {
  _id: string;
  name: string;
  rate: number;
  description?: string;
  isActive: boolean;
}

const ProfitMarginsPage: React.FC = () => {
  const [profitMargins, setProfitMargins] = useState<ProfitMargin[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProfitMargin, setEditingProfitMargin] = useState<ProfitMargin | null>(null);
  const [formData, setFormData] = useState({ name: '', rate: 0, description: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProfitMargins();
  }, []);

  const fetchProfitMargins = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProfitMargins();
      setProfitMargins(response.data.profitMargins || []);
    } catch (error: any) {
      console.error('Error fetching profit margins:', error);
      toast.error(error.response?.data?.error || 'Failed to fetch profit margins');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (profitMargin?: ProfitMargin) => {
    if (profitMargin) {
      setEditingProfitMargin(profitMargin);
      setFormData({ name: profitMargin.name, rate: profitMargin.rate, description: profitMargin.description || '' });
    } else {
      setEditingProfitMargin(null);
      setFormData({ name: '', rate: 0, description: '' });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingProfitMargin(null);
    setFormData({ name: '', rate: 0, description: '' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'rate' ? parseFloat(value) : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || formData.rate < 0) {
      toast.error('Profit margin name and a non-negative rate are required');
      return;
    }
    try {
      setSubmitting(true);
      if (editingProfitMargin) {
        await apiService.updateProfitMargin(editingProfitMargin._id, formData);
        toast.success('Profit margin updated successfully');
      } else {
        await apiService.createProfitMargin(formData);
        toast.success('Profit margin created successfully');
      }
      handleCloseModal();
      fetchProfitMargins();
    } catch (error: any) {
      console.error('Error saving profit margin:', error);
      toast.error(error.response?.data?.error || 'Failed to save profit margin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this profit margin?')) return;
    try {
      await apiService.deleteProfitMargin(id);
      toast.success('Profit margin deleted successfully');
      fetchProfitMargins();
    } catch (error: any) {
      console.error('Error deleting profit margin:', error);
      toast.error(error.response?.data?.error || 'Failed to delete profit margin');
    }
  };

  const filteredProfitMargins = profitMargins.filter(profitMargin =>
    profitMargin.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { header: 'Name', accessor: (row: ProfitMargin) => row.name },
    { header: 'Rate (%)', accessor: (row: ProfitMargin) => `${row.rate}%` },
    { header: 'Description', accessor: (row: ProfitMargin) => row.description || '-' },
    {
      header: 'Actions',
      accessor: (row: ProfitMargin) => (
        <div className="flex gap-0.5">
          <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" icon={<Edit2 className="w-4 h-4" />} onClick={() => handleOpenModal(row)} />
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" icon={<Trash2 className="w-4 h-4" />} onClick={() => handleDelete(row._id)} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>Profit Margins</h1>
          <p className="mt-2 text-sm text-gray-600" style={{ fontFamily: 'var(--font-family)' }}>Manage profit margin percentages</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add Profit Margin</Button>
      </motion.div>

      <Card padding="md">
        <Input
          placeholder="Search profit margins..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          icon={<Search className="w-5 h-5" />}
          fullWidth
        />
      </Card>

      <Card padding="none">
        {loading ? (
          <div className="flex justify-center items-center py-12"><LoadingSpinner size="lg" /></div>
        ) : filteredProfitMargins.length > 0 ? (
          <Table data={filteredProfitMargins} columns={columns} emptyMessage="No profit margins found" />
        ) : (
          <div className="px-6 py-12 text-center">
            <TrendingUp className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>No profit margins yet</h3>
            <p className="mt-1 text-sm text-gray-500" style={{ fontFamily: 'var(--font-family)' }}>Get started by adding your first profit margin.</p>
            <div className="mt-6">
              <Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add Profit Margin</Button>
            </div>
          </div>
        )}
      </Card>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingProfitMargin ? 'Edit Profit Margin' : 'Add New Profit Margin'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g., Standard Profit, Premium Profit"
            required
            fullWidth
            icon={<TrendingUp className="w-5 h-5 text-gray-400" />}
          />
          <Input
            label="Rate (%)"
            name="rate"
            type="number"
            value={formData.rate}
            onChange={handleChange}
            placeholder="e.g., 25"
            required
            fullWidth
            icon={<Percent className="w-5 h-5 text-gray-400" />}
          />
          <Input
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter description (optional)"
            fullWidth
            icon={<FileText className="w-5 h-5 text-gray-400" />}
          />
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={submitting}>Cancel</Button>
            <Button type="submit" loading={submitting}>{editingProfitMargin ? 'Update Profit Margin' : 'Create Profit Margin'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ProfitMarginsPage;

